// i18n dictionary: en + tr. Default: browser language, fallback en.
// All UI strings live here — screens must not hardcode copy.

export type Lang = 'en' | 'tr';

const dict = {
  en: {
    // top bar / shell
    'app.title': 'ColorSep — Textile Color Separation Tool',
    'nav.account': 'Account',
    'nav.billing': 'Billing',
    'nav.signout': 'Sign out',
    'nav.backToEditor': 'Back to editor',
    // auth
    'auth.login.title': 'Sign in',
    'auth.signup.title': 'Create your account',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.passwordAgain': 'Password (again)',
    'auth.signIn': 'Sign in',
    'auth.signUp': 'Create account',
    'auth.google': 'Continue with Google',
    'auth.forgot': 'Forgot your password?',
    'auth.reset.title': 'Reset password',
    'auth.reset.send': 'Send reset link',
    'auth.reset.newPassword': 'New password',
    'auth.verify.title': 'Verify your email',
    'auth.verify.body': 'Confirming your address…',
    'auth.verify.done': 'Email verified. Your 30-day trial has started.',
    'auth.verify.fail': 'Verification failed. Use the link from your inbox.',
    'err.invalidCredentials': 'Wrong email or password.',
    'err.emailTaken': 'This email is already registered.',
    'err.emailTakenRetry': 'An account for this email already exists — a trial was already started here once.',
    'err.checkEmail': 'Check your inbox to confirm your address.',
    'err.generic': 'Something went wrong. Please try again.',
    'err.passwordShort': 'Password must be at least 10 characters.',
    'err.passwordMismatch': 'Passwords do not match.',
    'err.emailInvalid': 'Enter a valid email address.',
    // banner / gating
    'banner.trial': '{n} day(s) left in your trial',
    'banner.grace': 'Offline — reconnect to refresh your license',
    'banner.expired': 'Your trial has ended. Export is disabled until you upgrade.',
    'banner.expired_sub': 'Your subscription is not active. Export is disabled until you upgrade.',
    'cta.upgrade': 'Upgrade',
    // pricing
    'pricing.title': 'Plans',
    'pricing.trialCta': '30-day free trial — no card required',
    'pricing.perMonth': '/mo',
    'pricing.perYear': '/yr',
    // toasts
    'toast.saved': 'Saved',
    'toast.deviceRevoked': 'Device revoked',
  },
  tr: {
    'app.title': 'ColorSep — Textile Color Separation Tool',
    'nav.account': 'Hesap',
    'nav.billing': 'Faturalama',
    'nav.signout': 'Çıkış yap',
    'nav.backToEditor': 'Editöre dön',
    'auth.login.title': 'Giriş yap',
    'auth.signup.title': 'Hesap oluştur',
    'auth.email': 'E-posta',
    'auth.password': 'Şifre',
    'auth.passwordAgain': 'Şifre (tekrar)',
    'auth.signIn': 'Giriş yap',
    'auth.signUp': 'Hesap oluştur',
    'auth.google': 'Google ile devam et',
    'auth.forgot': 'Şifreni mi unuttun?',
    'auth.reset.title': 'Şifre sıfırlama',
    'auth.reset.send': 'Sıfırlama bağlantısı gönder',
    'auth.reset.newPassword': 'Yeni şifre',
    'auth.verify.title': 'E-postanı doğrula',
    'auth.verify.body': 'Adresin doğrulanıyor…',
    'auth.verify.done': 'E-posta doğrulandı. 30 günlük denemen başladı.',
    'auth.verify.fail': 'Doğrulama başarısız. Gelen kutundaki bağlantıyı kullan.',
    'err.invalidCredentials': 'E-posta veya şifre hatalı.',
    'err.emailTaken': 'Bu e-posta zaten kayıtlı.',
    'err.emailTakenRetry': 'Bu e-posta için hesap zaten var — deneme daha önce başlatılmış.',
    'err.checkEmail': 'Adresini onaylamak için gelen kutunu kontrol et.',
    'err.generic': 'Bir şeyler ters gitti. Tekrar dene.',
    'err.passwordShort': 'Şifre en az 10 karakter olmalı.',
    'err.passwordMismatch': 'Şifreler uyuşmuyor.',
    'err.emailInvalid': 'Geçerli bir e-posta adresi gir.',
    'banner.trial': 'Denemenizde {n} gün kaldı',
    'banner.grace': 'Çevrimdışı — lisansı tazelemek için bağlanın',
    'banner.expired': 'Denemeniz sona erdi. Yükseltene kadar dışa aktarma kapalı.',
    'banner.expired_sub': 'Aboneliğiniz aktif değil. Yükseltene kadar dışa aktarma kapalı.',
    'cta.upgrade': 'Yükselt',
    'pricing.title': 'Planlar',
    'pricing.trialCta': '30 gün ücretsiz deneme — kart gerekmez',
    'pricing.perMonth': '/ay',
    'pricing.perYear': '/yıl',
    'toast.saved': 'Kaydedildi',
    'toast.deviceRevoked': 'Cihaz kaldırıldı',
  },
} as const;

const navigatorLang: Lang =
  typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('tr')
    ? 'tr'
    : 'en';

export const i18n = {
  lang: navigatorLang,
  t(key: string, vars: Record<string, string | number> = {}): string {
    const table = dict[navigatorLang];
    let s = (table as Record<string, string>)[key] ?? (dict.en as Record<string, string>)[key] ?? key;
    for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
    return s;
  },
};
